import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'

const TeamsContext = createContext(null)

let teamsCache = null
let teamsPromise = null

const fetchTeamsFromSupabase = async () => {
  const { data, error } = await supabase
    .from('teams')
    .select('id, name, code')

  if (error) {
    const message = error.message || 'Failed to load teams'
    const fetchError = new Error(message)
    fetchError.cause = error
    throw fetchError
  }

  return data ?? []
}

const requestTeams = (force = false) => {
  if (force) {
    teamsCache = null
    teamsPromise = null
  }

  if (teamsCache && !force) {
    return Promise.resolve(teamsCache)
  }

  if (!teamsPromise) {
    teamsPromise = fetchTeamsFromSupabase()
      .then((teams) => {
        teamsCache = teams
        return teams
      })
      .catch((error) => {
        throw error
      })
      .finally(() => {
        teamsPromise = null
      })
  }

  return teamsPromise
}

export const TeamsProvider = ({ children }) => {
  const [teams, setTeams] = useState(() => teamsCache ?? [])
  const [loading, setLoading] = useState(() => !teamsCache)
  const [error, setError] = useState(null)

  const refreshTeams = useCallback(async (force = false) => {
    if (!force && teamsCache) {
      setTeams(teamsCache)
      setError(null)
      return teamsCache
    }

    setLoading(true)

    try {
      const data = await requestTeams(force)
      setTeams(data)
      setError(null)
      return data
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!teamsCache) {
      refreshTeams().catch((err) => {
        console.error('Error loading teams:', err)
      })
    } else {
      setLoading(false)
    }
  }, [refreshTeams])

  const getTeamIdByCode = useCallback(
    (code) => {
      if (!code || !Array.isArray(teams)) return undefined
      const normalizedCode = code.trim().toLowerCase()
      const match = teams.find((team) => team.code?.toLowerCase() === normalizedCode)
      return match?.id
    },
    [teams]
  )

  const mapTeamIdentifiers = useCallback(() => {
    if (!Array.isArray(teams)) {
      return {
        byId: {},
        byCode: {},
        byName: {}
      }
    }

    return teams.reduce(
      (acc, team) => {
        if (!team) return acc

        if (team.id != null) {
          acc.byId[team.id] = team
        }

        if (team.code) {
          acc.byCode[team.code.toLowerCase()] = team
        }

        if (team.name) {
          acc.byName[team.name.toLowerCase()] = team
        }

        return acc
      },
      {
        byId: {},
        byCode: {},
        byName: {}
      }
    )
  }, [teams])

  const normalizeTeamIdentifiers = useCallback(
    (input) => {
      if (input === undefined || input === null) {
        return []
      }

      const values = Array.isArray(input) ? input.flat(Infinity) : [input]
      if (values.length === 0) {
        return []
      }

      const { byId, byCode, byName } = mapTeamIdentifiers()
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      const seen = new Set()
      const normalized = []

      const addId = (candidate) => {
        if (candidate === undefined || candidate === null) {
          return
        }

        const value = String(candidate).trim()
        if (!value) {
          return
        }

        const lookupId = byId[value]
        const resolvedId = lookupId?.id ?? value

        if (!uuidPattern.test(resolvedId) && !byId[resolvedId]) {
          return
        }

        if (!seen.has(resolvedId)) {
          seen.add(resolvedId)
          normalized.push(resolvedId)
        }
      }

      const resolveFromTeam = (team) => {
        if (!team || typeof team !== 'object') {
          return
        }

        if (team.id !== undefined && team.id !== null) {
          addId(team.id)
          return
        }

        if (team.team_id !== undefined && team.team_id !== null) {
          addId(team.team_id)
        }

        if (team.code) {
          const match = byCode[String(team.code).toLowerCase()]
          if (match?.id) {
            addId(match.id)
            return
          }
        }

        if (team.name) {
          const match = byName[String(team.name).toLowerCase()]
          if (match?.id) {
            addId(match.id)
            return
          }
        }

        if (team.team && typeof team.team === 'object') {
          resolveFromTeam(team.team)
        }
      }

      values.forEach((item) => {
        if (item === undefined || item === null) {
          return
        }

        if (typeof item === 'string') {
          const value = item.trim()
          if (!value) {
            return
          }

          if (uuidPattern.test(value)) {
            addId(value)
            return
          }

          const lowerValue = value.toLowerCase()
          const teamMatch = byCode[lowerValue] || byName[lowerValue]
          if (teamMatch?.id) {
            addId(teamMatch.id)
            return
          }

          if (byId[value]?.id) {
            addId(byId[value].id)
          }

          return
        }

        if (typeof item === 'object') {
          resolveFromTeam(item)
          return
        }

        addId(item)
      })

      return normalized
    },
    [mapTeamIdentifiers]
  )

  const contextValue = useMemo(
    () => ({
      teams,
      loading,
      error,
      refreshTeams,
      getTeamIdByCode,
      mapTeamIdentifiers,
      normalizeTeamIdentifiers
    }),
    [
      teams,
      loading,
      error,
      refreshTeams,
      getTeamIdByCode,
      mapTeamIdentifiers,
      normalizeTeamIdentifiers
    ]
  )

  return <TeamsContext.Provider value={contextValue}>{children}</TeamsContext.Provider>
}

export const useTeams = () => {
  const context = useContext(TeamsContext)

  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider')
  }

  return context
}

